import { useState } from 'react';
import {
  Users,
  Plus,
  Search,
  Filter,
  Lock,
  Unlock,
  Key,
  Trash2,
  X,
  AlertCircle,
  Copy,
  Check,
  Pencil,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { productsService } from '../services/products.service';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useBlockUser,
  useUnblockUser,
  useResetPassword,
  useDeleteUser,
} from '../hooks/useUsers';
import type {
  UserListItem,
  UserRole,
  CreateUserRequest,
  UpdateUserRequest,
} from '../services/users.service';
import { useDebounce } from '../hooks/useDebounce';

// ============================================
// Constants
// ============================================

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Admin',
  MANAGER: 'Gerente',
  SELLER: 'Vendedor',
};

const ROLE_COLORS: Record<UserRole, string> = {
  ADMIN: 'bg-subtle text-danger',
  MANAGER: 'bg-subtle text-info',
  SELLER: 'bg-subtle text-accent',
};

const ASSIGNABLE_ROLES: { value: UserRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MANAGER', label: 'Gerente' },
  { value: 'SELLER', label: 'Vendedor' },
];

// ============================================
// Sub-components
// ============================================

function TempPasswordModal({
  isOpen,
  password,
  onClose,
}: {
  isOpen: boolean;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-[14px] shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-ink-primary">Senha temporária gerada</h3>
          <button onClick={onClose} className="text-ink-tertiary hover:text-ink-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-ink-secondary mb-4 text-sm">
          Copie a senha abaixo e envie ao usuário. Esta senha não será exibida novamente.
        </p>
        <div className="flex items-center gap-2 p-3 bg-canvas rounded-[10px] border border-border">
          <code className="flex-1 font-mono text-sm text-ink-primary select-all">{password}</code>
          <button
            onClick={handleCopy}
            className="p-1.5 text-ink-tertiary hover:text-ink-primary rounded transition-colors"
            title="Copiar senha"
          >
            {copied ? <Check className="w-4 h-4 text-accent" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="btn-primary">
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  confirmVariant = 'danger',
  onConfirm,
  onCancel,
  isLoading,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmVariant?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-[14px] shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle
            className={clsx(
              'w-6 h-6',
              confirmVariant === 'danger' ? 'text-danger' : 'text-ink-primary'
            )}
          />
          <h3 className="text-lg font-semibold text-ink-primary">{title}</h3>
        </div>
        <p className="text-ink-secondary mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="btn-secondary" disabled={isLoading}>
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className={clsx('btn', confirmVariant === 'danger' ? 'btn-danger' : 'btn-primary')}
            disabled={isLoading}
          >
            {isLoading ? <LoadingSpinner size="sm" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductCheckboxes({
  productIds,
  onChange,
}: {
  productIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const { data } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsService.list(),
  });
  const products: any[] = data?.data || data || [];

  if (products.length === 0) {
    return null;
  }

  return (
    <div>
      <label className="block text-sm font-medium text-ink-secondary mb-2">Produtos</label>
      <div className="flex flex-wrap gap-2">
        {products.map((p: any) => (
          <label
            key={p.id}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border cursor-pointer transition-all',
              productIds.includes(p.id)
                ? 'bg-subtle text-ink-primary border-accent'
                : 'bg-canvas text-ink-tertiary border-border opacity-60'
            )}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={productIds.includes(p.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange([...productIds, p.id]);
                } else {
                  onChange(productIds.filter((id) => id !== p.id));
                }
              }}
            />
            {p.name}
          </label>
        ))}
      </div>
    </div>
  );
}

function CreateUserModal({
  isOpen,
  onClose,
  onTempPassword,
}: {
  isOpen: boolean;
  onClose: () => void;
  onTempPassword: (password: string) => void;
}) {
  const emptyForm: CreateUserRequest = { email: '', name: '', role: 'SELLER', productIds: [] };
  const [formData, setFormData] = useState<CreateUserRequest>(emptyForm);
  const createUser = useCreateUser();

  const handleClose = () => {
    setFormData(emptyForm);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await createUser.mutateAsync(formData);
      setFormData(emptyForm);
      onClose();
      if (response.temporaryPassword) {
        onTempPassword(response.temporaryPassword);
      }
    } catch {
      // Error handled by hook
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-[14px] shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-surface">
          <h3 className="text-lg font-semibold text-ink-primary">Novo Usuário</h3>
          <button onClick={handleClose} className="text-ink-tertiary hover:text-ink-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">E-mail</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
              placeholder="usuario@exemplo.com"
              required
            />
          </div>
          <div>
            <label className="label">Nome</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="Nome completo"
              required
              minLength={2}
            />
          </div>
          <div>
            <label className="label">Cargo</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="input"
              required
            >
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <ProductCheckboxes
            productIds={formData.productIds ?? []}
            onChange={(ids) => setFormData({ ...formData, productIds: ids })}
          />
          <p className="text-sm text-ink-tertiary">
            Uma senha temporária será gerada automaticamente e exibida após a criação.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={createUser.isPending}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createUser.isPending}>
              {createUser.isPending ? <LoadingSpinner size="sm" /> : 'Criar Usuário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({
  isOpen,
  user,
  onClose,
}: {
  isOpen: boolean;
  user: UserListItem | null;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState<UpdateUserRequest>({});
  const updateUser = useUpdateUser();

  const userId = user?.id;
  const [lastUserId, setLastUserId] = useState<number | null>(null);

  if (userId && userId !== lastUserId) {
    setLastUserId(userId);
    setFormData({
      name: user?.name,
      role: user?.role,
      productIds: (user as any)?.products?.map((p: any) => p.productId) ?? [],
    });
  }

  const handleClose = () => {
    setLastUserId(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      return;
    }
    try {
      await updateUser.mutateAsync({ id: user.id, data: formData });
      handleClose();
    } catch {
      // Error handled by hook
    }
  };

  if (!isOpen || !user) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-[14px] shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-surface">
          <h3 className="text-lg font-semibold text-ink-primary">Editar Usuário</h3>
          <button onClick={handleClose} className="text-ink-tertiary hover:text-ink-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">E-mail</label>
            <input type="email" value={user.email} className="input bg-canvas" disabled />
          </div>
          <div>
            <label className="label">Nome</label>
            <input
              type="text"
              value={formData.name ?? ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="Nome completo"
              required
              minLength={2}
            />
          </div>
          <div>
            <label className="label">Cargo</label>
            <select
              value={formData.role ?? user.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="input"
              required
            >
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <ProductCheckboxes
            productIds={formData.productIds ?? []}
            onChange={(ids) => setFormData({ ...formData, productIds: ids })}
          />
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={updateUser.isPending}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={updateUser.isPending}>
              {updateUser.isPending ? <LoadingSpinner size="sm" /> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserRow({
  user,
  onBlock,
  onUnblock,
  onResetPassword,
  onEdit,
  onDelete,
}: {
  user: UserListItem;
  onBlock: (user: UserListItem) => void;
  onUnblock: (user: UserListItem) => void;
  onResetPassword: (user: UserListItem) => void;
  onEdit: (user: UserListItem) => void;
  onDelete: (user: UserListItem) => void;
}) {
  return (
    <div className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-subtle flex items-center justify-center flex-shrink-0">
            <span className="text-ink-primary font-medium text-sm">
              {user.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-ink-primary">{user.name}</span>
              <span
                className={clsx(
                  'px-2 py-0.5 rounded text-xs font-medium',
                  ROLE_COLORS[user.role] ?? 'bg-subtle text-ink-secondary'
                )}
              >
                {ROLE_LABELS[user.role] ?? user.role}
              </span>
              {!user.active && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-subtle text-danger">
                  Bloqueado
                </span>
              )}
            </div>
            <span className="text-sm text-ink-tertiary">{user.email}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {user.active ? (
          <button
            onClick={() => onBlock(user)}
            className="p-2 text-ink-tertiary hover:text-warning hover:bg-subtle rounded-[10px] transition-colors"
            title="Bloquear usuário"
          >
            <Lock className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={() => onUnblock(user)}
            className="p-2 text-ink-tertiary hover:text-accent hover:bg-subtle rounded-[10px] transition-colors"
            title="Desbloquear usuário"
          >
            <Unlock className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => onEdit(user)}
          className="p-2 text-ink-tertiary hover:text-ink-primary hover:bg-subtle rounded-[10px] transition-colors"
          title="Editar usuário"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={() => onResetPassword(user)}
          className="p-2 text-ink-tertiary hover:text-ink-primary hover:bg-subtle rounded-[10px] transition-colors"
          title="Redefinir senha"
        >
          <Key className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(user)}
          className="p-2 text-ink-tertiary hover:text-danger hover:bg-subtle rounded-[10px] transition-colors"
          title="Excluir usuário"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============================================
// Main Page Component
// ============================================

export function UsersPage() {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    role: '' as UserRole | '',
    active: undefined as boolean | undefined,
    search: '',
  });
  const [searchInput, setSearchInput] = useState('');
  useDebounce(searchInput, 350, (val) => setFilters((f) => ({ ...f, search: val, page: 1 })));

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    type: 'block' | 'unblock' | 'reset' | 'delete';
    user: UserListItem | null;
  }>({ isOpen: false, type: 'block', user: null });

  const { data, isLoading, isError } = useUsers({
    page: filters.page,
    limit: filters.limit,
    role: filters.role || undefined,
    active: filters.active,
    search: filters.search || undefined,
  });

  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();
  const resetPassword = useResetPassword();
  const deleteUser = useDeleteUser();

  const closeConfirm = (type: 'block' | 'unblock' | 'reset' | 'delete') =>
    setConfirmDialog({ isOpen: false, type, user: null });

  const handleConfirmAction = async () => {
    if (!confirmDialog.user) {
      return;
    }
    const { type } = confirmDialog;

    try {
      switch (type) {
        case 'block':
          await blockUser.mutateAsync(confirmDialog.user.id);
          break;
        case 'unblock':
          await unblockUser.mutateAsync(confirmDialog.user.id);
          break;
        case 'reset': {
          const response = await resetPassword.mutateAsync(confirmDialog.user.id);
          setTempPassword(response.temporaryPassword);
          break;
        }
        case 'delete':
          await deleteUser.mutateAsync(confirmDialog.user.id);
          break;
      }
      closeConfirm(type);
    } catch {
      // Error handled by hooks
    }
  };

  const users = data?.users || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">Gerenciar Usuários</h1>
          <p className="text-ink-secondary">Administração de contas de usuários do sistema</p>
        </div>
        <button onClick={() => setIsCreateModalOpen(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Novo Usuário
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <Filter className="w-5 h-5 text-ink-tertiary" />
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por nome ou e-mail..."
              className="input pl-10"
            />
          </div>
          <select
            value={filters.role}
            onChange={(e) =>
              setFilters({ ...filters, role: e.target.value as UserRole | '', page: 1 })
            }
            className="input w-auto"
          >
            <option value="">Todos os cargos</option>
            {ASSIGNABLE_ROLES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={filters.active === undefined ? '' : String(filters.active)}
            onChange={(e) =>
              setFilters({
                ...filters,
                active: e.target.value === '' ? undefined : e.target.value === 'true',
                page: 1,
              })
            }
            className="input w-auto"
          >
            <option value="">Todos os status</option>
            <option value="true">Ativos</option>
            <option value="false">Bloqueados</option>
          </select>
        </div>
      </div>

      {/* Users List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : isError ? (
        <div className="card text-center py-12">
          <AlertCircle className="w-12 h-12 text-danger mx-auto mb-4" />
          <p className="text-ink-secondary">Erro ao carregar usuários. Tente novamente.</p>
        </div>
      ) : users.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="w-12 h-12 text-ink-tertiary mx-auto mb-4" />
          <p className="text-ink-secondary">Nenhum usuário encontrado</p>
        </div>
      ) : (
        <div className="card divide-y divide-border">
          {users.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              onBlock={(u) => setConfirmDialog({ isOpen: true, type: 'block', user: u })}
              onUnblock={(u) => setConfirmDialog({ isOpen: true, type: 'unblock', user: u })}
              onResetPassword={(u) => setConfirmDialog({ isOpen: true, type: 'reset', user: u })}
              onEdit={(u) => setEditingUser(u)}
              onDelete={(u) => setConfirmDialog({ isOpen: true, type: 'delete', user: u })}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            disabled={filters.page === 1}
            className="btn-secondary"
          >
            Anterior
          </button>
          <span className="text-ink-secondary">
            Página {filters.page} de {data.totalPages}
          </span>
          <button
            onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            disabled={filters.page === data.totalPages}
            className="btn-secondary"
          >
            Próxima
          </button>
        </div>
      )}

      {/* Modals */}
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onTempPassword={(pwd) => setTempPassword(pwd)}
      />

      <EditUserModal
        isOpen={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
      />

      <TempPasswordModal
        isOpen={!!tempPassword}
        password={tempPassword ?? ''}
        onClose={() => setTempPassword(null)}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen && confirmDialog.type === 'block'}
        title="Bloquear Usuário"
        message={`Tem certeza que deseja bloquear ${confirmDialog.user?.name}? O usuário não poderá acessar o sistema.`}
        confirmLabel="Bloquear"
        confirmVariant="danger"
        onConfirm={handleConfirmAction}
        onCancel={() => closeConfirm('block')}
        isLoading={blockUser.isPending}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen && confirmDialog.type === 'unblock'}
        title="Desbloquear Usuário"
        message={`Tem certeza que deseja desbloquear ${confirmDialog.user?.name}? O usuário poderá acessar o sistema novamente.`}
        confirmLabel="Desbloquear"
        confirmVariant="primary"
        onConfirm={handleConfirmAction}
        onCancel={() => closeConfirm('unblock')}
        isLoading={unblockUser.isPending}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen && confirmDialog.type === 'reset'}
        title="Redefinir Senha"
        message={`Tem certeza que deseja redefinir a senha de ${confirmDialog.user?.name}? Uma nova senha temporária será gerada.`}
        confirmLabel="Redefinir"
        confirmVariant="primary"
        onConfirm={handleConfirmAction}
        onCancel={() => closeConfirm('reset')}
        isLoading={resetPassword.isPending}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen && confirmDialog.type === 'delete'}
        title="Excluir Usuário"
        message={`Tem certeza que deseja excluir ${confirmDialog.user?.name}? O usuário será desativado permanentemente.`}
        confirmLabel="Excluir"
        confirmVariant="danger"
        onConfirm={handleConfirmAction}
        onCancel={() => closeConfirm('delete')}
        isLoading={deleteUser.isPending}
      />
    </div>
  );
}

export default UsersPage;
